/**
 * Scraping Rules Panel
 *
 * Displays and manages domain-specific scraping rules in the Settings page.
 * Shows rules created by LLM parsing with success/failure tracking.
 */

import { useState } from 'react';
import {
    Paper,
    Stack,
    Group,
    Text,
    Title,
    Card,
    Badge,
    Button,
    ActionIcon,
    Modal,
    TextInput,
    Loader,
    Center,
    Alert,
    Progress,
    Code,
    Accordion,
    Table,
    Switch,
    Box,
} from '@mantine/core';
import {
    IconTrash,
    IconRefresh,
    IconCheck,
    IconX,
    IconAlertCircle,
    IconWorld,
    IconRobot,
    IconUser,
    IconSparkles,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import {
    useScrapingRules,
    useToggleScrapingRule,
    useDeleteScrapingRule,
    useTestScrapingRule,
    useLearnFromUrl,
    ScrapingRule,
} from '../../api/scrapingRules';
import { useUserSettings, useUpdateUserSettings } from '../../api/userSettings';

export function ScrapingRulesPanel() {
    const { data, isLoading, error, refetch } = useScrapingRules();
    const toggleMutation = useToggleScrapingRule();
    const deleteMutation = useDeleteScrapingRule();
    const testMutation = useTestScrapingRule();
    const learnMutation = useLearnFromUrl();

    // User settings for direct LLM mode
    const { data: userSettings, isLoading: settingsLoading } = useUserSettings();
    const updateSettingsMutation = useUpdateUserSettings();

    const [testModalOpen, setTestModalOpen] = useState(false);
    const [testUrl, setTestUrl] = useState('');
    const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);
    const [learnModalOpen, setLearnModalOpen] = useState(false);
    const [learnUrl, setLearnUrl] = useState('');

    const rules = data?.data || [];
    const stats = data?.stats;
    const directLlmMode = userSettings?.use_direct_llm_parsing ?? false;

    const handleDirectLlmToggle = async (checked: boolean) => {
        try {
            await updateSettingsMutation.mutateAsync({ use_direct_llm_parsing: checked });
            notifications.show({
                title: checked ? 'Direct LLM Mode Enabled' : 'Direct LLM Mode Disabled',
                message: checked
                    ? 'Job parsing will now use LLM directly without creating rules'
                    : 'Job parsing will use domain-specific rules when available',
                color: 'blue',
            });
        } catch (error) {
            notifications.show({
                title: 'Error',
                message: error instanceof Error ? error.message : 'Failed to update setting',
                color: 'red',
            });
        }
    };

    const handleToggle = async (id: string) => {
        try {
            await toggleMutation.mutateAsync(id);
            notifications.show({
                title: 'Rule Updated',
                message: 'Rule status toggled successfully',
                color: 'green',
            });
        } catch (error) {
            notifications.show({
                title: 'Error',
                message: error instanceof Error ? error.message : 'Failed to toggle rule',
                color: 'red',
            });
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Are you sure you want to delete this rule?')) return;

        try {
            await deleteMutation.mutateAsync(id);
            notifications.show({
                title: 'Rule Deleted',
                message: 'Scraping rule has been deleted',
                color: 'green',
            });
        } catch (error) {
            notifications.show({
                title: 'Error',
                message: error instanceof Error ? error.message : 'Failed to delete rule',
                color: 'red',
            });
        }
    };

    const handleTest = async () => {
        if (!selectedRuleId || !testUrl) return;

        try {
            const result = await testMutation.mutateAsync({
                ruleId: selectedRuleId,
                testUrl,
            });

            notifications.show({
                title: result.success ? 'Test Passed' : 'Test Failed',
                message: result.success
                    ? `Extracted ${result.fieldsExtracted.length} fields with ${(result.confidence * 100).toFixed(0)}% confidence`
                    : `Missing fields: ${result.fieldsMissing.join(', ')}`,
                color: result.success ? 'green' : 'red',
            });

            setTestModalOpen(false);
            setTestUrl('');
        } catch (error) {
            notifications.show({
                title: 'Test Error',
                message: error instanceof Error ? error.message : 'Failed to test rule',
                color: 'red',
            });
        }
    };

    const handleLearn = async () => {
        if (!learnUrl) return;

        try {
            const result = await learnMutation.mutateAsync(learnUrl);

            notifications.show({
                title: 'Rule Created',
                message: `Learned rule for ${result.domain} with ${(result.confidence * 100).toFixed(0)}% confidence`,
                color: 'green',
            });

            setLearnModalOpen(false);
            setLearnUrl('');
        } catch (error) {
            notifications.show({
                title: 'Learning Failed',
                message: error instanceof Error ? error.message : 'Failed to learn from URL',
                color: 'red',
            });
        }
    };

    const openTestModal = (ruleId: string) => {
        setSelectedRuleId(ruleId);
        setTestModalOpen(true);
    };

    if (isLoading) {
        return (
            <Paper p="lg" radius="md" withBorder>
                <Center>
                    <Loader />
                </Center>
            </Paper>
        );
    }

    if (error) {
        return (
            <Paper p="lg" radius="md" withBorder>
                <Alert icon={<IconAlertCircle size={16} />} color="red">
                    Failed to load scraping rules: {error.message}
                </Alert>
            </Paper>
        );
    }

    return (
        <Paper p="lg" radius="md" withBorder>
            <Stack gap="md">
                {/* Header */}
                <Group justify="space-between">
                    <div>
                        <Title order={2} size="h3">
                            Scraping Rules
                        </Title>
                        <Text size="sm" c="dimmed">
                            Domain-specific rules for extracting job data. Rules are automatically
                            created when parsing new domains.
                        </Text>
                    </div>
                    <Group gap="xs">
                        <Button
                            leftSection={<IconRefresh size={16} />}
                            variant="default"
                            size="sm"
                            onClick={() => refetch()}
                        >
                            Refresh
                        </Button>
                        <Button
                            leftSection={<IconRobot size={16} />}
                            variant="light"
                            size="sm"
                            onClick={() => setLearnModalOpen(true)}
                            disabled={directLlmMode}
                        >
                            Learn from URL
                        </Button>
                    </Group>
                </Group>

                {/* Direct LLM Mode Toggle */}
                <Box
                    p="md"
                    style={{
                        backgroundColor: directLlmMode
                            ? 'var(--mantine-color-blue-light)'
                            : 'var(--mantine-color-gray-light)',
                        borderRadius: 'var(--mantine-radius-md)',
                    }}
                >
                    <Group justify="space-between" align="flex-start">
                        <div style={{ flex: 1 }}>
                            <Group gap="xs" mb={4}>
                                <IconSparkles size={18} />
                                <Text fw={500}>Parse Directly with LLM</Text>
                            </Group>
                            <Text size="sm" c="dimmed">
                                When enabled, all job parsing uses LLM directly instead of domain-specific
                                rules. More reliable but uses API tokens for each job added.
                            </Text>
                            {directLlmMode && (
                                <Alert
                                    mt="sm"
                                    p="xs"
                                    color="blue"
                                    variant="light"
                                    icon={<IconAlertCircle size={14} />}
                                >
                                    <Text size="xs">
                                        Domain rules below will be ignored while this mode is enabled.
                                    </Text>
                                </Alert>
                            )}
                        </div>
                        <Switch
                            checked={directLlmMode}
                            onChange={(event) => handleDirectLlmToggle(event.currentTarget.checked)}
                            disabled={settingsLoading || updateSettingsMutation.isPending}
                            size="md"
                        />
                    </Group>
                </Box>

                {/* Stats */}
                {stats && (
                    <Group gap="lg">
                        <Badge size="lg" variant="light" color="blue">
                            {stats.total} Total Rules
                        </Badge>
                        <Badge size="lg" variant="light" color="green">
                            {stats.active} Active
                        </Badge>
                        <Badge size="lg" variant="light" color="gray">
                            {stats.inactive} Inactive
                        </Badge>
                        <Badge size="lg" variant="light" color="violet">
                            {(stats.avgConfidence * 100).toFixed(0)}% Avg Confidence
                        </Badge>
                    </Group>
                )}

                {/* Rules List */}
                {rules.length === 0 ? (
                    <Alert icon={<IconWorld size={16} />} color="blue">
                        No scraping rules yet. Rules will be automatically created when you add jobs
                        from new domains using LLM parsing.
                    </Alert>
                ) : (
                    <Accordion variant="separated">
                        {rules.map((rule) => (
                            <RuleCard
                                key={rule.id}
                                rule={rule}
                                onToggle={handleToggle}
                                onDelete={handleDelete}
                                onTest={openTestModal}
                            />
                        ))}
                    </Accordion>
                )}
            </Stack>

            {/* Test Rule Modal */}
            <Modal
                opened={testModalOpen}
                onClose={() => setTestModalOpen(false)}
                title="Test Scraping Rule"
                size="md"
            >
                <Stack gap="md">
                    <TextInput
                        label="Test URL"
                        description="Enter a job posting URL from this domain to test the rule"
                        placeholder="https://..."
                        value={testUrl}
                        onChange={(e) => setTestUrl(e.currentTarget.value)}
                    />
                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => setTestModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleTest}
                            loading={testMutation.isPending}
                            disabled={!testUrl}
                        >
                            Test Rule
                        </Button>
                    </Group>
                </Stack>
            </Modal>

            {/* Learn from URL Modal */}
            <Modal
                opened={learnModalOpen}
                onClose={() => setLearnModalOpen(false)}
                title="Learn Scraping Rule from URL"
                size="md"
            >
                <Stack gap="md">
                    <Alert icon={<IconRobot size={16} />} color="blue">
                        This will use your LLM provider to analyze the page and create a scraping
                        rule for the domain.
                    </Alert>
                    <TextInput
                        label="Job Posting URL"
                        description="Enter a job posting URL to learn from"
                        placeholder="https://..."
                        value={learnUrl}
                        onChange={(e) => setLearnUrl(e.currentTarget.value)}
                    />
                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => setLearnModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleLearn}
                            loading={learnMutation.isPending}
                            disabled={!learnUrl}
                        >
                            Learn Rule
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Paper>
    );
}

// ============================================================================
// Rule Card Component
// ============================================================================

interface RuleCardProps {
    rule: ScrapingRule;
    onToggle: (id: string) => void;
    onDelete: (id: string) => void;
    onTest: (id: string) => void;
}

function RuleCard({ rule, onToggle, onDelete, onTest }: RuleCardProps) {
    const successRate =
        rule.success_count + rule.failure_count > 0
            ? (rule.success_count / (rule.success_count + rule.failure_count)) * 100
            : 0;

    return (
        <Accordion.Item value={rule.id}>
            <Accordion.Control>
                <Group justify="space-between" wrap="nowrap" style={{ flex: 1 }}>
                    <Group gap="sm">
                        <IconWorld size={18} />
                        <div>
                            <Text fw={500}>{rule.domain}</Text>
                            <Text size="xs" c="dimmed">
                                v{rule.version} • Created{' '}
                                {new Date(rule.created_at).toLocaleDateString()}
                            </Text>
                        </div>
                    </Group>
                    <Group gap="xs">
                        <Badge
                            size="sm"
                            color={rule.is_active ? 'green' : 'gray'}
                            variant="light"
                        >
                            {rule.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                        <Badge
                            size="sm"
                            color={
                                rule.confidence_score > 0.8
                                    ? 'green'
                                    : rule.confidence_score > 0.5
                                      ? 'yellow'
                                      : 'red'
                            }
                            variant="light"
                        >
                            {(rule.confidence_score * 100).toFixed(0)}%
                        </Badge>
                        <Badge
                            size="sm"
                            leftSection={
                                rule.created_by === 'llm' ? (
                                    <IconRobot size={12} />
                                ) : (
                                    <IconUser size={12} />
                                )
                            }
                            variant="outline"
                        >
                            {rule.created_by === 'llm' ? 'LLM' : 'Manual'}
                        </Badge>
                    </Group>
                </Group>
            </Accordion.Control>
            <Accordion.Panel>
                <Stack gap="md">
                    {/* Stats */}
                    <Group gap="lg">
                        <div>
                            <Text size="xs" c="dimmed">
                                Success Rate
                            </Text>
                            <Group gap="xs">
                                <Progress
                                    value={successRate}
                                    size="sm"
                                    style={{ width: 100 }}
                                    color={successRate > 80 ? 'green' : successRate > 50 ? 'yellow' : 'red'}
                                />
                                <Text size="sm">{successRate.toFixed(0)}%</Text>
                            </Group>
                        </div>
                        <div>
                            <Text size="xs" c="dimmed">
                                Uses
                            </Text>
                            <Group gap={4}>
                                <IconCheck size={14} color="green" />
                                <Text size="sm">{rule.success_count}</Text>
                                <IconX size={14} color="red" style={{ marginLeft: 8 }} />
                                <Text size="sm">{rule.failure_count}</Text>
                            </Group>
                        </div>
                        <div>
                            <Text size="xs" c="dimmed">
                                Last Used
                            </Text>
                            <Text size="sm">
                                {rule.last_used_at
                                    ? new Date(rule.last_used_at).toLocaleDateString()
                                    : 'Never'}
                            </Text>
                        </div>
                    </Group>

                    {/* Selectors */}
                    <div>
                        <Text size="sm" fw={500} mb="xs">
                            CSS Selectors
                        </Text>
                        <Table withTableBorder>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>Field</Table.Th>
                                    <Table.Th>Selector</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                <Table.Tr>
                                    <Table.Td>Title</Table.Td>
                                    <Table.Td>
                                        <Code fz="xs">{rule.selectors.title?.selector}</Code>
                                    </Table.Td>
                                </Table.Tr>
                                <Table.Tr>
                                    <Table.Td>Company</Table.Td>
                                    <Table.Td>
                                        <Code fz="xs">{rule.selectors.company?.selector}</Code>
                                    </Table.Td>
                                </Table.Tr>
                                <Table.Tr>
                                    <Table.Td>Description</Table.Td>
                                    <Table.Td>
                                        <Code fz="xs">{rule.selectors.description?.selector}</Code>
                                    </Table.Td>
                                </Table.Tr>
                                {rule.selectors.location && (
                                    <Table.Tr>
                                        <Table.Td>Location</Table.Td>
                                        <Table.Td>
                                            <Code fz="xs">{rule.selectors.location.selector}</Code>
                                        </Table.Td>
                                    </Table.Tr>
                                )}
                                {rule.selectors.salary && (
                                    <Table.Tr>
                                        <Table.Td>Salary</Table.Td>
                                        <Table.Td>
                                            <Code fz="xs">{rule.selectors.salary.selector}</Code>
                                        </Table.Td>
                                    </Table.Tr>
                                )}
                            </Table.Tbody>
                        </Table>
                    </div>

                    {/* Actions */}
                    <Group justify="flex-end" gap="xs">
                        <Button size="xs" variant="light" onClick={() => onTest(rule.id)}>
                            Test
                        </Button>
                        <Button
                            size="xs"
                            variant="light"
                            color={rule.is_active ? 'gray' : 'green'}
                            onClick={() => onToggle(rule.id)}
                        >
                            {rule.is_active ? 'Deactivate' : 'Activate'}
                        </Button>
                        <ActionIcon
                            color="red"
                            size="sm"
                            variant="light"
                            onClick={() => onDelete(rule.id)}
                        >
                            <IconTrash size={16} />
                        </ActionIcon>
                    </Group>
                </Stack>
            </Accordion.Panel>
        </Accordion.Item>
    );
}
